import {Button, CircularProgress, MenuItem} from '@material-ui/core';
import {ModalState, showModal} from 'Core/ducks/modal';
import {createSnackbar} from 'Core/ducks/snackbar';
import {Empty, ServerError, AsyncError, isLoaded} from 'Core/models/Async';
import {
  changeExternalUploadQueueFilters,
  changeEnrollmentApplications,
  FilterEnum,
  cancelExternalUploadQueue,
  getExternalUploadQueue,
  clearExternalUploadQueueFilters,
  processBrokerSpreadsheet,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import {EnrollmentFileStatusType} from 'Enrollment/models/EnrollmentFileStatusType';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import Column from 'Shared/components/PagedView/Column';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {
  withDateFilterProperties,
  withInequalityFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import RequireAtLeastOneScope from 'Shared/components/RequireAtLeastOneScope';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {downloadFile} from 'Shared/utilities/fetchHelpers';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './ExternalUploadQueue.sass';
import {ExternalUploadEnrollmentApplication} from 'Enrollment/models/ExternalUploadEnrollmentApplication';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIconWithCustomError} from 'Shared/components/Toolbar/ToolbarHeader';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import useReactRouter from 'use-react-router';

const ExternalUploadQueue = () => {
  const {externalUploadQueue, externalUploadQueueFilters} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );
  const {userClaims} = useSelector((state: ReducerState) => state.user.profile);
  const scopes = useSelector(selectUserScopes);
  const [downloadingFileId, setDownloadingFileId] = useState<string | undefined>(undefined);
  const [getActionBy, setActionBy] = useState('');
  const dispatch = useDispatch();
  const {history} = useReactRouter();
  const changeFilter = withValueChangedHook(externalUploadQueueFilters, changeExternalUploadQueueFilters, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: externalUploadQueueFilters,
  };

  useEffect(() => {   
    dispatch(getExternalUploadQueue(undefined, externalUploadQueueFilters));
    if(isLoaded(userClaims)){
      setActionBy(userClaims.Data.firstName + ' ' + userClaims.Data.lastName);
    }
  }, [dispatch, externalUploadQueueFilters]);

  useEffect(() => {
    return () => {
      dispatch(clearExternalUploadQueueFilters());
      dispatch(changeEnrollmentApplications(new Empty()));
    };
  }, [dispatch]);

  const fileStatusOption: FilterEnum[] = [
    {name: 'Received', value: 'Received'},
    {name: 'Processing', value: 'Processing'},
    {name: 'Completed', value: 'Completed'},
    {name: 'Canceled', value: 'Canceled'},
    {name: 'Returned', value: 'Returned'},
  ];

  const handleDownloadFile = (enrollmentFile: ExternalUploadEnrollmentApplication) => {
    const extension = enrollmentFile.fileType === '.xlsx' ? FileExtension.XLSX : FileExtension.XLS;
    const relativeFileLocation = `/enrollmentFiles/${enrollmentFile.id}/export/xlsx`;

    downloadFile(relativeFileLocation, `${enrollmentFile.fileName!}${extension}`)
      .then((response: AsyncError | string) => {
        if (typeof response === 'string') {
          dispatch(createSnackbar(response));
        } else {
          if (response instanceof ServerError)
            dispatch(createSnackbar('An Error Occurred While Downloading the Enrollment File.'));
          else dispatch(createSnackbar(response.message));
        }
      })
      .then(() => {
        externalUploadQueueFilters.actionBy = getActionBy;
        dispatch(getExternalUploadQueue(undefined, externalUploadQueueFilters));
        setDownloadingFileId(undefined);
      });

    setDownloadingFileId(enrollmentFile.id!);
  };
  const handleEnrollmentApplicationCancellation = (fileId: string, fileStatusId: number) => {
    if (fileId && isLoaded(userClaims)) {
      dispatch(cancelExternalUploadQueue(fileId, fileStatusId, externalUploadQueueFilters, undefined, undefined, undefined, getActionBy));
    }
  };

  const hanldleProcessBrokerSpreadsheet = (fileId: string) => {
    dispatch(processBrokerSpreadsheet(fileId));
  };
  return (
    <ModularHost>
      <ToolbarHeader title="Broker Spreadsheet Uploads">
        <ExportToolbarHeaderIconWithCustomError
          title="Export Broker Spreadsheet  Records"
          downloadedFileName="Broker Spreadsheet"
          extension={FileExtension.CSV}
          relativeFileLocation={`/enrollmentFiles/externalUploadQueue/csv/${addFilterParameters({
            ...externalUploadQueueFilters,
            exportToCsv: true,
          })}`}
          totalRecordCount={isLoaded(externalUploadQueue) ? externalUploadQueue.Data.totalCount : 0}
        />
      </ToolbarHeader>
      <PagedView
        pagedViewKey="externalUpload"
        data={externalUploadQueue}
        loadMore={page => dispatch(getExternalUploadQueue(page, externalUploadQueueFilters))}
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="File Name"
              filterProperties={withStringFilterProperties(filterProperties, 'fileName')}
              default
            >
              {enrollmentFiles => enrollmentFiles.fileName}
            </Column>
            <Column
              {...columnProps}
              title="Account Number"
              filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
            >
              {enrollmentFiles => enrollmentFiles.accountNumber}
            </Column>
            <Column
              {...columnProps}
              title="Date Uploaded"
              filterProperties={withDateFilterProperties(filterProperties, 'fileUploadedDate')}
              default
            >
              {enrollmentFiles => toFormattedDate(enrollmentFiles.fileUploadedDate!)}
            </Column>
            <Column
              {...columnProps}
              title="Uploaded By"
              filterProperties={withStringFilterProperties(filterProperties, 'uploadedByUserName')}
              default
            >
              {enrollmentFiles => (
                <Button
                  color="primary"
                  onClick={() =>
                    dispatch(
                      showModal(
                        ModalState.UserDetails({
                          firstName: enrollmentFiles.uploadedByFirstName!,
                          lastName: enrollmentFiles.uploadedByLastName!,
                          email: enrollmentFiles.uploadedByEmail!,
                        })
                      )
                    )
                  }
                >
                  {enrollmentFiles.uploadedByUserName}
                </Button>
              )}
            </Column>
            <Column
              {...columnProps}
              title="Number of Records"
              filterProperties={withInequalityFilterProperties(filterProperties, 'numberOfRecords')}
            >
              {enrollmentFiles => enrollmentFiles.recordCount}
            </Column>
            <Column
              {...columnProps}
              title="Status"
              filterProperties={withSelectFilterProperties(filterProperties, 'fileStatuses', fileStatusOption, true)}
              default
            >
              {enrollmentFiles =>
                enrollmentFiles.fileStatus?.name === 'Returned' ? (
                  <Button
                    color="primary"
                    onClick={() =>
                      dispatch(
                        showModal(
                          ModalState.ExternalUploadReturnToBroker({
                            removalReason: enrollmentFiles.removalReason!,
                            returnComments: enrollmentFiles.returnComments!,
                            recentActionDate: enrollmentFiles.recentActionDate,
                          })
                        )
                      )
                    }
                  >
                    Returned
                  </Button>
                ) : (
                  <Button color="primary" disabled>
                    {enrollmentFiles.processReason?.name === 'Processing'
                      ? enrollmentFiles.processReason?.name
                      : enrollmentFiles.fileStatus?.name}
                  </Button>
                )
              }
            </Column>
            <Column
              {...columnProps}
              title="Last Action"
              filterProperties={withDateFilterProperties(filterProperties, 'recentActionDate')}
              default
            >
              {enrollmentFiles => toFormattedDate(enrollmentFiles.recentActionDate!)}
            </Column>
            <Column {...columnProps} title="Actions" default>
              {enrollmentFiles => (
                <RequireAtLeastOneScope
                  scopes={[
                    Scope.CompleteExternalEnrollmentFile,
                    Scope.RemoveExternalEnrollmentFile,
                    Scope.DownloadEnrollmentFile,
                    Scope.ProcessEnrollmentFile,
                  ]}
                >
                  {downloadingFileId === enrollmentFiles.id ? (
                    <CircularProgress />
                  ) : (
                    <ActionsMenu>
                      <div>
                        <RequireScope scopes={scopes} scope={Scope.DownloadEnrollmentFile}>
                          <MenuItem
                            disabled={Boolean(downloadingFileId)}
                            color="primary"
                            onClick={() => handleDownloadFile(enrollmentFiles)}
                          >
                            Download
                          </MenuItem>
                        </RequireScope>
                        <RequireScope scopes={scopes} scope={Scope.ProcessEnrollmentFile}>
                          <MenuItem
                            color="primary"
                            onClick={() =>
                              dispatch(
                                showModal(
                                  ModalState.ExternalUploadQueueProcessConfirmation({
                                    fileId: enrollmentFiles.id!,
                                  })
                                )
                              )
                            }
                          >
                            Process
                          </MenuItem>
                        </RequireScope>
                        <RequireScope scopes={scopes} scope={Scope.CompleteExternalEnrollmentFile}>
                          <MenuItem
                            color="primary"
                            onClick={() =>
                              handleEnrollmentApplicationCancellation(
                                enrollmentFiles.id!,
                                EnrollmentFileStatusType.COMPLETED
                              )
                            }
                          >
                            Complete
                          </MenuItem>
                        </RequireScope>
                        <RequireScope scopes={scopes} scope={Scope.RemoveExternalEnrollmentFile}>
                          <MenuItem
                            color="primary"
                            onClick={() =>
                              dispatch(
                                showModal(
                                  ModalState.ExternalUploadQueueRemoveConfirmation({
                                    fileId: enrollmentFiles.id!,
                                    uploadedByEmail: enrollmentFiles.uploadedByEmail!
                                  })
                                )
                              )
                            }
                          >
                            Return to Broker
                          </MenuItem>
                        </RequireScope>
                        <MenuItem
                      onClick={() =>
                        history.push(
                          EnrollmentRouteConstants.EXTERNAL_UPLOAD_QUEUE_DETAILS(
                            enrollmentFiles.id!
                          )
                        )
                      }
                    >
                      View History
                    </MenuItem>
                      </div>
                    </ActionsMenu>
                  )}
                </RequireAtLeastOneScope>
              )}
            </Column>
          </>
        )}
      </PagedView>
    </ModularHost>
  );
};

export default ExternalUploadQueue;
